<template>
    <v-text-field
        ref="inputBaseRef"
        v-bind="$attrs"
        v-on="listeners"
        :value="formattedValue"
        :rules="rulesNumberValue"
    >
        <template v-for="(_, slot) of $slots" v-slot:[slot]>
            <slot :name="slot"></slot>
        </template>
    </v-text-field>
</template>

<script>
import {useCurrencyInput} from 'vue-currency-input';
import {watch, ref, onMounted} from 'vue';
import _ from "lodash";

export default {
    name: "VCurrencyInput",
    props: {
        value: Number,
        options: {
            type: Object,
            default: () => {
                return {valueRange: {min: 0, max: 1e12}};
            }
        },
        rules: {
            type: Array,
            default: () => [],
        },
    },
    setup(props) {
        const defaultOptions = {
            locale: 'ru-RU',
            currency: 'RUB',
            currencyDisplay: 'hidden',
            valueRange: undefined,
            precision: 2,
            hideCurrencySymbolOnFocus: true,
            hideGroupingSeparatorOnFocus: false,
            hideNegligibleDecimalDigitsOnFocus: true,
            autoDecimalDigits: false,
            exportValueAsInteger: false,
            autoSign: true,
            useGrouping: true
        };

        /**
         * при реализаци тех. консультации - карточка - бюджет - чекбокс "бюджет неизвестен".
         * возникла проблема если в v-currency-input добавить в слоты prepend или prepend-inner компонент v-checkbox.
         * то v-currency-input ломается(можно вводить не цифры, вводимое значение не обновляется через v-model).
         * происходит это по следующей причине:
         * если inputRef указывает на компонент, то useCurrencyInput
         * привязывается к первому input, по сути input для привязки вычисляется так:
         * inputRef.value.$el.querySelector('input')
         * и useCurrencyInput привязывается к input из v-checkbox вместо input из v-text-field.
         * см: node_modules/vue-currency-input/dist/index.esm.js строки 441,473
         *
         * решение:
         * composition-api при монтировании в inputBaseRef положит ссылку на базовый компонент.
         * потом сработает обработчик onMounted
         * и в нем в inputRef положим ссылку на привильный input текстового поля для useCurrencyInput
         * потом сработает обработчик onMounted внутри useCurrencyInput и возьмет правильный input из inputRef
         **/
        const inputBaseRef = ref(null);
        onMounted(() => {
            /**
             * если баовый компонент например v-text-field-copy
             * то либо в нем добавлять ref на его базовай компонент и искать в нем.
             * либо через querySelector('input[type=text]')
             **/
            if (!inputBaseRef?.value) {
                return;
            }
            inputRef.value = inputBaseRef.value.$refs.input || inputBaseRef.value.$el.querySelector('input[type=text]');
        });

        const {
            inputRef,
            formattedValue,
            setOptions,
            setValue,
            numberValue,
        } = useCurrencyInput(Object.assign({}, defaultOptions, props.options));

        watch(() => props.options, (value, prev) => {
            /**
             * В node_modules/vue-currency-input/dist/index.esm.js:231
             * для каждого setOption зачем-то вызывается
             * this.applyFixedFractionFormat(this.numberValue, true);
             * что влечет за собой бесконечное добавление ,00.
             *
             * Если передаваемые option не изменились, то не вызываем setOption
             */
            if (JSON.stringify(value) === JSON.stringify(prev)) {
                return;
            }
            /**
             * Если передаваемые option изменились, то склеиваем из
             * с default-значениями...
             */
            value = Object.assign({}, defaultOptions, value);
            prev = Object.assign({}, defaultOptions, prev);
            /**
             * ... и проверяем, поменялись ли реальные option элемента
             * управления - если нет, не вызываем setOption
             */
            if (JSON.stringify(value) === JSON.stringify(prev)) {
                return;
            }

            setOptions(value);
        });

        watch(() => props.value, (value) => {
            setValue(value)
        });


        return {inputBaseRef, formattedValue, numberValue}
    },
    computed: {
        listeners() {
            return _.omit(this.$listeners, ['input']);
        },
        rulesNumberValue() {
            /**
             * v-text-field в rules передает строку для валидации
             * подменяем на числовое значение
             **/
            return _.map(this.rules, (rule) => () => rule(this.numberValue));
        },
    },
    methods: {
        validate(...args) {
            this.$refs?.inputBaseRef?.validate(...args);
        },
    },
}
</script>

<style scoped>
.v-text-field >>> .v-text-field__slot input {
    text-align: right;
    padding-right: 4px;
}
</style>
